import React, { useState } from 'react';

import { List } from '@buildoutinc/blueprint-react/ui/List';
import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';

import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { post } from 'helpers/api';
import { pluralize } from 'helpers/formatters/string_formats';

import { reasonFrom } from './api_reason';
import OrientationBadge from './orientation_badge';
import PdfDropzone, { DROP_REJECTED } from './pdf_dropzone';
import type { Orientation } from './types';

export interface ReplaceTarget {
  id: number;
  name: string;
  orientation: Orientation;
  fileName: string | null;
}

export interface AffectedDocument {
  id: number;
  name: string;
}

interface Props {
  basePath: string;
  companyId: number | string | null;
  page: ReplaceTarget;
  documents: AffectedDocument[];
  documentCount: number;
  show: boolean;
  onClose: () => void;
  onReplaced: (documentCount: number) => void;
}

interface ReplacedResponse {
  documentsCount: number;
}

const REPLACE_FAILED = "The file couldn't be replaced. Please try again.";

const documentsPhrase = (count: number) => pluralize(count, 'document');

// Two steps: pick the file, then confirm against every document that uses the
// page. The file is held here until confirm, so nothing changes server-side
// until the one request that replaces it everywhere.
export default function ReplaceFileModal({
  basePath, companyId, page, documents, documentCount, show, onClose, onReplaced,
}: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const companyQuery = companyId ? `?company_id=${companyId}` : '';

  const close = () => {
    if (busy) return;

    onClose();
  };

  const handleDrop = (files: File[]) => {
    const dropped = files[0];
    if (!dropped) return;

    setError(null);
    setFile(dropped);
  };

  const confirm = async () => {
    if (!file) return;

    const body = new FormData();
    body.append('pdf', file);

    setBusy(true);
    setError(null);
    try {
      const response: ReplacedResponse = await post(`${basePath}/pdf/${page.id}/file${companyQuery}`, body);
      onReplaced(response.documentsCount);
    } catch (requestError) {
      // Back to step one: the reason is about the file, so the fix is a different file.
      setFile(null);
      setError(reasonFrom(requestError) ?? REPLACE_FAILED);
    } finally {
      setBusy(false);
    }
  };

  const currentFile = (
    // File names are often one long unbroken token, so the row wraps and the
    // names break rather than pushing past the modal's edge.
    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
      <span className="fw-semibold text-break">{page.name}</span>
      <OrientationBadge orientation={page.orientation} />
      <span className="text-muted text-break">{page.fileName || 'Unknown file'}</span>
    </div>
  );

  if (file) {
    return (
      <Modal open={show} onClose={close}>
        <Modal.Content centered>
          <Modal.Header>
            <Modal.Title>Replace in {documentsPhrase(documentCount)}?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {currentFile}
            <p>
              <span className="fw-semibold text-break">{file.name}</span> replaces the current file everywhere this
              page is used.
            </p>
            {documents.length > 0 && (
              <List className="mb-3" flush>
                {documents.map(doc => (
                  <List.Item key={doc.id}>{doc.name}</List.Item>
                ))}
              </List>
            )}
            <p className="text-muted mb-0">
              Documents created from now on use the new file. Documents already created keep the version they
              were built with.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button disabled={busy} variant="ghost" onClick={close}>Cancel</Button>
            <Button disabled={busy} variant="secondary" onClick={() => setFile(null)}>Back</Button>
            <Button disabled={busy} variant="primary" onClick={confirm}>
              {busy ? 'Replacing...' : `Replace in ${documentsPhrase(documentCount)}`}
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
    );
  }

  return (
    <Modal open={show} onClose={close}>
      <Modal.Content centered>
        <Modal.Header>
          <Modal.Title>Replace file</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentFile}
          {error && (
            <Alert className="mb-3" severity="destructive">
              <Alert.Title>{error}</Alert.Title>
            </Alert>
          )}
          <PdfDropzone
            busy={busy}
            hint={`A ${page.orientation} PDF up to 50MB`}
            prompt={<>Drop the new file here or <span className="text-primary">click to choose it</span></>}
            onDrop={handleDrop}
            onDropRejected={() => setError(DROP_REJECTED)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={busy} variant="ghost" onClick={close}>Cancel</Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
